import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useBookmarks } from "@/context/BookmarksContext";
import PronounceButton from "@/components/PronounceButton";
import CopyButton from "@/components/CopyButton";
import { buildVerseText, printSimpleDocument } from "@/lib/studyText";
import { Loader2, ChevronRight, Clock, Bookmark, BookOpen, Languages, Trash2, ArrowRight, Printer, Eye, Layers, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const EMPTY_IMG = "https://images.unsplash.com/photo-1530018607912-eff2daa1bac4";

export default function History() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("studies"); // "studies" | "saved"
  const [sessions, setSessions] = useState(null);
  const [passages, setPassages] = useState([]);
  const [opening, setOpening] = useState("");
  const { bookmarks, loaded, remove } = useBookmarks();

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: p }] = await Promise.all([
          api.get("/study/sessions"),
          api.get("/study/passages"),
        ]);
        setSessions(s);
        setPassages(p || []);
      } catch {
        setSessions([]);
      }
    })();
  }, []);

  const openPassage = async (ref) => {
    if (!ref) return;
    setOpening(ref);
    try {
      const { data } = await api.post("/study/start", { input: ref, mode: "passage" });
      navigate(`/study/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't open that passage");
      setOpening("");
    }
  };

  const verses = bookmarks.filter((b) => b.kind === "verse");
  const words = bookmarks.filter((b) => b.kind === "word");

  return (
    <div className="px-6 py-10 space-y-8 md:max-w-2xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header>
        <h1 className="text-3xl text-white">Your journey</h1>
        <p className="text-sm text-slate-400 mt-2">Every session you've walked through, and the verses & words you've saved.</p>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-[#141B29] rounded-xl">
        {[["studies", "Studies", Clock], ["saved", "Saved", Bookmark]].map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`history-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
            {id === "saved" && bookmarks.length > 0 && (
              <span className="ml-1 text-[10px] bg-[#10222A] text-[#00E5FF] rounded-full px-1.5 py-0.5">{bookmarks.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* STUDIES */}
      {tab === "studies" && (
        sessions === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-10 space-y-6">
            <img src={EMPTY_IMG} alt="" className="w-40 h-40 object-cover rounded-3xl mx-auto opacity-80" />
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">
              No studies yet. Head to the Study tab and open the Word — your history will gather here.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-studies">
            {/* Passages revisited across multiple sessions — grouped for review + combined export */}
            {passages.filter((p) => p.count > 1).length > 0 && (
              <section data-testid="revisited-passages" className="space-y-2 pb-2">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                  <Layers className="w-4 h-4" strokeWidth={1.5} /> Passages you've revisited
                </div>
                <div className="flex flex-wrap gap-2">
                  {passages.filter((p) => p.count > 1).map((p) => (
                    <button
                      key={p.key}
                      data-testid={`revisited-${p.key.replace(/\s+/g, "-")}`}
                      onClick={() => navigate(`/passages/${encodeURIComponent(p.key)}`)}
                      aria-label={`${p.label} — ${p.count} ${p.count === 1 ? "study" : "studies"}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#00E5FF]/30 bg-[#10222A]/60 pl-3 pr-2.5 py-1.5 text-sm text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                    >
                      {p.label}
                      <span className="text-[10px] bg-[#00E5FF] text-[#07090E] rounded-full px-1.5 py-0.5">{p.count}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {sessions.map((s) => (
              <button
                key={s.id}
                data-testid={`history-item-${s.id}`}
                onClick={() => navigate(s.kind === "deeper" ? `/deeper/${s.id}` : `/study/${s.id}`)}
                className="w-full text-left bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 flex items-center justify-between gap-3 hover:border-[#00E5FF]/30 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-lg text-slate-200 truncate flex items-center gap-2">
                    {s.kind === "deeper" && <MessageCircle className="w-4 h-4 text-[#00E5FF] shrink-0" strokeWidth={1.75} />}
                    {s.title || s.reference}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    {s.kind === "deeper" ? (
                      <span className="text-[#00E5FF]">Go Deeper{s.chat_count ? ` · ${s.chat_count} messages` : ""}</span>
                    ) : (
                      <>
                        <span className="text-[#00E5FF]">{s.reference}</span>
                        <span>·</span>
                        <span className="capitalize">{s.depth}</span>
                      </>
                    )}
                    <span>·</span>
                    <span>{new Date(s.created_at).toLocaleDateString()}</span>
                    {s.share_views > 0 && (
                      <>
                        <span>·</span>
                        <span data-testid={`history-views-${s.id}`} className="inline-flex items-center gap-1 text-[#00E5FF]">
                          <Eye className="w-3 h-3" strokeWidth={1.75} /> {s.share_views} {s.share_views === 1 ? "view" : "views"}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )
      )}

      {/* SAVED */}
      {tab === "saved" && (
        !loaded ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-12 space-y-4" data-testid="saved-empty">
            <div className="w-14 h-14 rounded-2xl bg-[#10222A] flex items-center justify-center mx-auto">
              <Bookmark className="w-6 h-6 text-[#00E5FF]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">
              Nothing saved yet. Tap the bookmark on a verse or an original-language word in any study, and it'll wait for you here.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="history-saved">
            {/* Saved verses */}
            {verses.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                  <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Verses
                </div>
                {verses.map((b) => (
                  <div key={b.id} data-testid={`saved-verse-${b.id}`} className="group bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 hover:border-[#00E5FF]/30 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-lg text-[#00E5FF]">{b.reference}</span>
                      <div className="flex items-center gap-1">
                        {b.translation && <span className="text-[10px] uppercase tracking-widest text-slate-500">{b.translation}</span>}
                        <CopyButton text={buildVerseText(b)} label="Verse copied" testid={`saved-copy-${b.id}`} />
                        <button
                          data-testid={`saved-print-${b.id}`}
                          onClick={() => printSimpleDocument({ title: b.reference, meta: b.translation || "", body: b.text || "", italic: false, cta: { phrase: "Save & study verses free with OpenWord AI", medium: "pdf", campaign: "verse_export" } })}
                          title="Print / save as PDF"
                          className="p-2 rounded-lg text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                        >
                          <Printer className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button data-testid={`saved-remove-${b.id}`} onClick={() => remove(b.id)} title="Remove" className="p-1.5 text-slate-500 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    {b.text && <p className="text-base text-slate-300 mt-1.5 leading-relaxed line-clamp-4">{b.text}</p>}
                    <button
                      data-testid={`saved-open-${b.id}`}
                      onClick={() => openPassage(b.reference)}
                      disabled={!!opening}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#00E5FF] hover:underline disabled:opacity-50"
                    >
                      {opening === b.reference ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
                      Study this passage
                    </button>
                  </div>
                ))}
              </section>
            )}

            {/* Saved words */}
            {words.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                  <Languages className="w-4 h-4" strokeWidth={1.5} /> Original-language words
                </div>
                {words.map((b) => (
                  <div key={b.id} data-testid={`saved-word-${b.id}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-xl text-slate-200">{b.original}</span>
                        <PronounceButton entry={b} testid={`saved-pronounce-${b.id}`} />
                        <span className="text-sm text-slate-400 truncate">{b.transliteration || b.word}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {b.strongs_number && <span className="text-[11px] font-medium text-[#00E5FF]">{b.strongs_number}</span>}
                        <button data-testid={`saved-remove-${b.id}`} onClick={() => remove(b.id)} title="Remove" className="p-1.5 text-slate-500 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    {b.definition && <p className="text-sm text-slate-400 mt-2 leading-relaxed">{b.definition}</p>}
                  </div>
                ))}
              </section>
            )}
          </div>
        )
      )}
    </div>
  );
}
